import type { UploadedImage } from "./uploads";
import { translate } from "@/i18n";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";

const uploadFailedStatus = (status: number) =>
  translate(libErrorsMessages, "uploadFailedStatus", { status });

/** Progress represents bytes transferred; 100% still awaits server acceptance. */
export function uploadWithProgress(
  url: string,
  headers: Record<string, string>,
  body: FormData,
  onProgress: (percent: number) => void,
): Promise<UploadedImage> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", url);
    request.withCredentials = true;
    for (const [key, value] of Object.entries(headers))
      request.setRequestHeader(key, value);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0)
        onProgress(
          Math.min(100, Math.round((event.loaded / event.total) * 100)),
        );
    };
    request.onerror = () =>
      reject(new Error(translate(libErrorsMessages, "uploadFailedConnection")));
    request.onabort = () =>
      reject(new Error(translate(libErrorsMessages, "uploadCancelled")));
    request.onload = () => {
      let result: { data?: UploadedImage; error?: string };
      try {
        result = JSON.parse(request.responseText);
      } catch {
        reject(new Error(uploadFailedStatus(request.status)));
        return;
      }
      if (request.status < 200 || request.status >= 300 || !result.data) {
        reject(new Error(result.error || uploadFailedStatus(request.status)));
        return;
      }
      resolve(result.data);
    };
    request.send(body);
  });
}
